import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useStaticQuery, graphql, Link } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { useWindowScroll, useLockBodyScroll } from 'react-use'
import { gsap, Power3 } from 'gsap'

import PromoBar from './PromoBar'
import NavDrawer from './NavDrawer'
import CartDrawer from './CartDrawer'
import Spinner from '../shared/Spinner'
import { MonoP, InternalLink } from '../shared/text'
import { colors, fonts } from '../../styles/variables'
import { media } from '../../styles/util'

import { trackViewCartEvent } from '../../utils/event-tracking'
import DogCircle from 'src/assets/img/icons/dog-circle.svg'
import LargeNav from './LargeNav'
import useCart from 'src/hooks/useCart'

const Header = styled.header`
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;

  .hidden {
    transform: translate(0, -200%);
  }
`

const Nav = styled('nav')<{ transparentBg: boolean; topOfPage: boolean }>`
  background-color: #fff;
  box-shadow: ${({ topOfPage }) => (topOfPage ? 'none' : '0 2px 2px -2px rgba(0, 0, 0, 0.2)')};
  width: 100%;
`

const HeaderInner = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  margin: 0 auto;
  align-items: center;
  ${media.mobile`
    padding:5px 20px;
    grid-template-columns: 1fr 1fr 1fr;
    grid-template-rows: 33px;
  `}
  ${media.tablet`
    padding: 15px 20px;
    grid-template-rows: 45px;
    grid-template-columns: 1fr 105px 1fr;
  `}
  ${media.desktop`
    padding: 5px 40px;
  `}
`

const QuizButton = styled(Link)`
  color: ${colors.navy};
  font-family: ${fonts.larsseit.medium};
  font-size: 18px;
  padding: 10px 30px;
  text-decoration: none;
  &:hover {
    color: ${colors.orange};
  }
`

const LogoLink = styled(InternalLink)`
  transition: 200ms transform ease-in-out;
  text-align: left;
  height: auto;
  &:hover {
    transform: scale(1.02);
  }
  ${media.mobile`
    width: 50px;
    grid-column: 2;
    align-self: center;
    justify-self: center;
  `}
  ${media.tablet`
    width: 100px;
    grid-column: 2;
  `}

  ${media.desktop`
    width: 80px;
    grid-column: 2;
    align-self: center;
    justify-self: center;
  `}
`
const ShopBtn = styled('button')`
  padding: 3px 30px;
  border-radius: 999em;
  background-color: ${colors.navy};
  font-family: ${fonts.larsseit.medium};
  font-size: 18px;
  color: #fff;
  font-weight: 500;
  border: none;
  cursor: pointer;
`

const RightGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`

const Count = styled(MonoP)`
  font-weight: bolder;
  color: ${colors.white};
  position: absolute;
  left: 50%;
  transform: translate(-50%, 0%);
  z-index: 10;

  ${media.mobile`
    top: 27%;
    left: 55%;
    font-size: 8px;
  `}
  ${media.tablet`
    top: 32%;
  `}
  ${media.desktop`
    top: 28%;
    font-size: 10px;
    left: 55%;
  `}
`

const CartCounter = styled.div`
  cursor: pointer;
  transition: 200ms color ease-in-out;
  position: relative;
  ${media.mobile`
    margin: 0 12px 0 16px;
    & svg {
      height: 36px;
      width: 32px;
    }
  `}
  ${media.tablet`
    margin-right: 12px;
    & svg {
      height: 40px;
      width: 36px;
    }
  `}
  ${media.desktop`
    margin-left: 20px;
  `}
  &:hover {
    & svg path {
      fill: ${colors.orange};
    }
    & ${Count} {
      transition: 200ms color ease-in-out;
      color: ${colors.white};
    }
  }
`

const MobileHide = styled.div`
  ${media.mobile`
    display: none;
  `}
  ${media.tablet`
    display: inline;
  `}
`

const DesktopHide = styled.div`
  ${media.mobile`
    display: inline;
  `}
  ${media.tablet`
    display: none;
  `}
`

const DrawerMask = styled.div`
  ${media.mobile`
    display: none;
  `}
  ${media.tablet`
    height: 100vh;
    width: 100vw;
    position: fixed;
    top: 0;
    left: 0;
    background-color: #000;
    opacity: 1;
    z-index: 999;
  `}
`

const AccountButtonGroup = styled('a')`
  ${media.mobile`
    img {
      width: 24px;
      height: 24px;
      object-fit: contain;
    }
  `}
  ${media.tablet`
    display: grid;
    place-items: center;
    grid-row-gap: 3px;
    text-align: center;
    color: ${colors.navy};
  `}
  ${media.desktop`
    img {
      width: 31px;
      height: 31px;
      object-fit: contain;
    }
  `}
`

const MobileNavBtn = styled.button`
  background-color: transparent;
  display: flex;
  align-items: center;
  border: none;
`

const query = graphql`
  query {
    logoImg: file(relativePath: { regex: "/logo-navy.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 400)
      }
    }
    dog: file(relativePath: { regex: "/logos/sitting-dog.png/" }) {
      childImageSharp {
        gatsbyImageData(width: 40)
      }
    }
  }
`

const NavBar = ({ pathname }: { pathname: string }) => {
  const images = useStaticQuery(query)

  const [navDrawerVisible, setNavDrawerVisible] = useState(false)
  const [cartDrawerVisible, setCartDrawerVisible] = useState(false)
  const [showNav, setShowNav] = useState(false)

  useLockBodyScroll(navDrawerVisible || cartDrawerVisible)
  const { y: scrollY } = useWindowScroll()
  const alwaysWhiteNavRoutes = ['/']
  const transparentBg = !scrollY && !alwaysWhiteNavRoutes.includes(pathname)
  const topOfPage = scrollY < 3

  const { updating: cartUpdating, quantity, cartState, items, cart, products } = useCart()

  useEffect(() => {
    if (cartUpdating) setCartDrawerVisible(true)
  }, [cartUpdating])

  useEffect(() => {
    gsap.from('.NavBar__Nav', { y: -125, ease: Power3.easeOut, delay: 0.25, duration: 0.75 })
  }, [])

  const handleCartOpen = () => {
    trackViewCartEvent(products, items, cart.cost.subtotalAmount?.amount)
    setCartDrawerVisible(true)
  }

  const handleCartClose = () => setCartDrawerVisible(false)
  const handleNavDrawerOpen = () => setNavDrawerVisible(true)
  const handleNavDrawerClose = () => setNavDrawerVisible(false)
  const handleShowNav = () => setShowNav((showNav) => !showNav)

  return (
    <>
      <Header>
        {(navDrawerVisible || cartDrawerVisible) && <DrawerMask />}
        <PromoBar
          copy="Free shipping on all orders over $40"
          bg={colors.navy}
          color={colors.lightText}
          hoverColor={colors.navy}
          linkUrl="/products"
        />
        <Nav transparentBg={transparentBg} topOfPage={topOfPage} className="NavBar__Nav">
          <HeaderInner>
            <MobileHide>
              {/* <NavButton copy="Menu" onClick={handleNavDrawerOpen} /> */}
              <ShopBtn onClick={handleShowNav}>Shop</ShopBtn>
              <QuizButton to={'/consultation'}>Take Quiz</QuizButton>
            </MobileHide>

            <DesktopHide>
              <MobileNavBtn onClick={handleNavDrawerOpen}>
                <svg
                  width="20"
                  height="18"
                  viewBox="0 0 20 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="19.8387" height="4.06452" rx="2.03226" fill="#1F1646" />
                  <rect y="6.96777" width="19.8387" height="4.06452" rx="2.03226" fill="#1F1646" />
                  <rect y="13.9355" width="19.8387" height="4.06452" rx="2.03226" fill="#1F1646" />
                </svg>
              </MobileNavBtn>
            </DesktopHide>

            <LogoLink to="/">
              <GatsbyImage
                image={getImage(images.logoImg)}
                alt="Finn logo"
                style={{ height: '100%', justifyContent: 'flex-start' }}
                imgStyle={{ objectFit: 'contain' }}
              />
            </LogoLink>

            <RightGroup>
              <AccountButtonGroup as="a" href="https://shop.petfinn.com/account">
                <img src={DogCircle} alt="Dog Circle" />
              </AccountButtonGroup>

              <CartCounter onClick={handleCartOpen} className="NavBar_CartCounter">
                <svg
                  width="36"
                  height="31"
                  viewBox="0 0 36 31"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M15.3866 24.7295C13.7813 24.7295 12.4805 26.0286 12.4805 27.6318C12.4805 29.235 13.7813 30.5333 15.3866 30.5333C16.9918 30.5333 18.2918 29.2342 18.2918 27.6318C18.2918 26.0295 16.991 24.7295 15.3866 24.7295Z"
                    fill="#161345"
                  />
                  <path
                    d="M25.9598 24.7295C24.3546 24.7295 23.0537 26.0286 23.0537 27.6318C23.0537 29.235 24.3546 30.5333 25.9598 30.5333C27.5651 30.5333 28.8651 29.2342 28.8651 27.6318C28.8651 26.0295 27.5642 24.7295 25.9598 24.7295Z"
                    fill="#161345"
                  />
                  <path
                    d="M29.0768 18.7015C29.1159 18.6616 29.1491 18.6168 29.1782 18.5694C29.1782 18.5694 29.1782 18.5686 29.1782 18.5677C29.1866 18.5536 29.1949 18.5395 29.2015 18.5245C29.234 18.4664 29.2589 18.4058 29.2772 18.341L33.3552 7.86882C33.6089 7.21675 33.386 6.47581 32.8138 6.0721C32.4337 5.8038 31.9795 5.6601 31.5146 5.6601H9.30381L8.11775 1.29248C7.94641 0.734274 7.43157 0.351339 6.84602 0.348847L1.43555 0.322266C0.728574 0.324758 0.187943 0.718491 0.190438 1.36225C0.193765 2.00602 0.737723 2.65725 1.44387 2.65393L6.2114 2.77106L11.661 23.3582H29.1258C29.9859 23.3582 30.6837 22.8656 30.6837 22.2592C30.6837 21.6528 29.9859 21.1602 29.1258 21.1602H13.465L12.9003 19.0769H28.2009C28.5569 19.0769 28.873 18.9291 29.0759 18.7023L29.0768 18.7015Z"
                    fill="#161345"
                  />
                  <circle cx="33.2215" cy="5.94419" r="2.42954" fill="#FF7F00" />
                </svg>

                {cartState.fetching ? (
                  <Spinner overlay size={30} color={colors.navy} />
                ) : (
                  <Count>{quantity}</Count>
                )}
              </CartCounter>
            </RightGroup>
          </HeaderInner>
          <MobileHide>
            <LargeNav visible={showNav} onClickOutside={() => setShowNav(false)} />
          </MobileHide>
        </Nav>

        <NavDrawer visible={navDrawerVisible} close={handleNavDrawerClose} pathname={pathname} />
        <CartDrawer visible={cartDrawerVisible} close={handleCartClose} />
      </Header>
    </>
  )
}

export default NavBar
